import useProperty from '@/hooks/useProperty';
import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/shared/Button';
import Card from '@/components/shared/Card';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import {
  ListOfUnits,
  type ListOfUnitsRef,
} from '@/components/dashboard/AddUnit/ListOfUnits';
import CreateLeaseInviteModal from '@/components/dashboard/AddUnit/CreateLeaseInviteModal';
import './PropertyPage.scss';
import PropertySearchSection from '@/components/dashboard/Property/PropertySearchSection';
import PropertyPageHeader from '@/components/dashboard/Property/PropertyPageHeader';
import PropertyOverview from '@/components/dashboard/Property/PropertyOverview';
import PropertyStatistics from '@/components/dashboard/Property/PropertyStatistics';
import PropertyInformation from '@/components/dashboard/Property/PropertyInformation';
import { useCollapsibleSections } from '@/hooks/useCollapsibleSections';
import UIConfirmationModal from '@/components/shared/UIConfirmationModal';
import { revokeInviteRequest } from '@/api/invite'; // Import revokeInviteRequest
import usePropertyStatistics from '@/hooks/usePropertyStatistics'; // Import the new hook

ChartJS.register(ArcElement, Tooltip, Legend);

const baseClass = 'PropertyPage';

// Interface for the selected unit
interface SelectedUnitForLease {
  unitId: number;
  unitNumber: string;
}

// Custom hook for revoking an invitation
const useRevokeInvite = (
  onSuccess?: () => void,
  onError?: (message: string) => void,
) => {
  const [isRevoking, setIsRevoking] = useState(false);

  const revokeInvite = useCallback(
    async (unitId: number, unitNumber: string) => {
      setIsRevoking(true);
      try {
        await revokeInviteRequest(unitId);
        console.log(`Invitation for unit ${unitNumber} successfully revoked.`); // Temporary console log
        onSuccess?.();
      } catch (error: any) {
        console.error('Error revoking invite:', error);
        const errorMessage =
          error.response?.data?.message || 'Error revoking invitation.';
        console.error(errorMessage); // Temporary console log
        onError?.(errorMessage);
      } finally {
        setIsRevoking(false);
      }
    },
    [onSuccess, onError],
  );

  return { revokeInvite, isRevoking };
};

const PropertyPage = () => {
  const navigate = useNavigate();
  // const profile = useSelector(profileSelector); // Removed unused variable

  const { property, propertyId, isLoading, isFetching } = useProperty();
  const { statistics, isLoading: isStatisticsLoading } = usePropertyStatistics(propertyId); // Use the new hook
  const listOfUnitsRef = useRef<ListOfUnitsRef>(null);

  // Managing section collapse states
  const { getSectionState, toggleSection } = useCollapsibleSections(
    'property-page',
    {
      overview: false,
      statistics: false,
      information: false,
    },
  );

  // States for CreateLeaseInviteModal
  const [showCreateLeaseModal, setShowCreateLeaseModal] = useState(false);
  const [selectedUnitForLease, setSelectedUnitForLease] =
    useState<SelectedUnitForLease | null>(null);

  // States for revoking an invitation
  const [showRevokeInviteModal, setShowRevokeInviteModal] = useState(false);
  const [unitToRevoke, setUnitToRevoke] = useState<{
    unitId: number;
    unitNumber: string;
  } | null>(null);

  const handleRevokeSuccess = useCallback(() => {
    listOfUnitsRef.current?.refetch(); // Refresh the list of units
    setShowRevokeInviteModal(false);
    setUnitToRevoke(null);
  }, []);

  const handleRevokeError = useCallback((message: string) => {
    // Here you would typically show a toast/notification
    // For now, it's handled by console.error in useRevokeInvite
    setShowRevokeInviteModal(false);
    setUnitToRevoke(null);
  }, []);

  const { revokeInvite, isRevoking } = useRevokeInvite(
    handleRevokeSuccess,
    handleRevokeError,
  );

  // const units: IUnit[] = property?.units || []; // Removed unused variable

  if (!propertyId || (!(isLoading || isFetching) && !property)) {
    return (
      <Card className={baseClass}>
        <h4>Invalid property ID</h4>
        <Button onClick={() => navigate('/properties')}>
          Go back to properties
        </Button>
      </Card>
    );
  }

  // Handler for unit actions
  const handleUnitAction = (
    unitId: string,
    action: string,
    unitNumber?: string,
  ) => {
    const numericUnitId = parseInt(unitId, 10);

    switch (action) {
      case 'add-tenant':
      case 'create-lease':
        // Opening the lease creation modal
        setSelectedUnitForLease({
          unitId: numericUnitId,
          unitNumber: unitNumber || `Unit ${unitId}`,
        });
        setShowCreateLeaseModal(true);
        break;

      case 'revoke-invite':
        setUnitToRevoke({
          unitId: numericUnitId,
          unitNumber: unitNumber || `Unit ${unitId}`,
        });
        setShowRevokeInviteModal(true);
        break;

      case 'tenant-view':
      case 'tenant-edit':
        // TODO: add logic for viewing/editing tenant
        console.log('Tenant action:', action, 'for unit:', unitId);
        break;

      case 'info':
        navigate(`/dashboard/properties/${propertyId}/units/${unitId}`);
        break;

      default:
        console.log('Unknown action:', action);
    }
  };

  // Handler for adding a new unit
  const handleAddUnit = () => {
    navigate(`/dashboard/properties/${propertyId}/units/add`);
  };

  // Handler for successful lease creation
  const handleLeaseCreated = () => {
    console.log('Lease created successfully');

    // Refreshing the list of units to see changes
    listOfUnitsRef.current?.refetch();
  };

  // Handler for closing the lease creation modal
  const handleCloseCreateLeaseModal = () => {
    setShowCreateLeaseModal(false);
    setSelectedUnitForLease(null);
  };

  const handleConfirmRevoke = () => {
    if (unitToRevoke) {
      revokeInvite(unitToRevoke.unitId, unitToRevoke.unitNumber);
    }
  };

  return (
    <>
      <PropertyPageHeader
        property={property}
        propertyId={propertyId}
        loading={isLoading || isFetching}
      />



      {/* Render PropertyOverview ONLY when there is a property */}
      {property && (
        <PropertyOverview
          property={property}
          loading={isLoading || isFetching}
          collapsed={getSectionState('overview')}
          onToggleCollapse={() => toggleSection('overview')}
          showMap={true}
        />
      )}

      {/* Render PropertyStatistics ONLY when there is a property */}
      {property && (
        <PropertyStatistics
          property={property}
          statistics={statistics} // Pass the statistics data
          loading={isLoading || isFetching || isStatisticsLoading} // Update loading state
          collapsed={getSectionState('statistics')}
          onToggleCollapse={() => toggleSection('statistics')}
        />
      )}
      {/* Render PropertyInformation ONLY when there is a property */}
      {property && (
        <PropertyInformation
          property={property}
          loading={isLoading || isFetching}
          collapsed={getSectionState('information')}
          onToggleCollapse={() => toggleSection('information')}
          editable={true}
          onEdit={() => navigate(`/dashboard/properties/${propertyId}/edit`)}
        />
      )}


      <PropertySearchSection
        loading={isLoading || isFetching}
        variant="wrapped"
      />


      <ListOfUnits
        ref={listOfUnitsRef}
        propertyId={propertyId}
        onUnitAction={handleUnitAction}
        onAddUnit={handleAddUnit}
        itemsPerPage={10}
      />

      {/* Modal for creating a lease */}
      {selectedUnitForLease && (
        <CreateLeaseInviteModal
          isOpen={showCreateLeaseModal}
          onClose={handleCloseCreateLeaseModal}
          propertyId={propertyId}
          unitId={selectedUnitForLease.unitId}
          unitNumber={selectedUnitForLease.unitNumber}
          propertyName={property?.name}
          onSuccess={handleLeaseCreated}
        />
      )}

      {/* Modal for revoking an invitation */}
      {unitToRevoke && (
        <UIConfirmationModal
          isOpen={showRevokeInviteModal}
          onClose={() => {
            setShowRevokeInviteModal(false);
            setUnitToRevoke(null);
          }}
          onConfirm={handleConfirmRevoke}
          title="Revoke Invitation"
          message={`Are you sure you want to revoke the invitation for unit ${unitToRevoke.unitNumber}? This action cannot be undone.`}
          confirmText="Revoke"
          cancelText="Cancel"
          isConfirming={isRevoking}
        />
      )}


    </>
  );
};

export default PropertyPage;
